import { FatSettingsPageSystemSettingsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select from '@/components/basicComponents/select';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { EditInvestorFormFieldsWrapper, EditInvestorModalPageWrapper } from '@/components/investors-page/editInvestorPage/styles';
import { CREATE_TENANT_SETTING_OVERRIDE_MUTATION, UPDATE_SYSTEM_SETTING_MUTATION } from '@/components/settings-page/queries';
import { AddressComponent } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/rowComponents/addressComponent';
import { ThreeColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { COUNTRY_CALLING_CODES_MAP } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { contactInfoSchema, IFirmInfoFormData } from '../types';

interface EditContactInfoModalPageProps {
  contactInfoFormData: IFirmInfoFormData['contactInfo'];
  firmInfoId: string | null;
  tenantId: string;
  firmInfoRefetch: () => Promise<ApolloQueryResult<FatSettingsPageSystemSettingsQuery>>;
  handleCloseModalPage: () => void;
}

export const EditContactInfoModalPage = ({
  contactInfoFormData,
  firmInfoId,
  tenantId,
  firmInfoRefetch,
  handleCloseModalPage
}: EditContactInfoModalPageProps) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const methods = useForm<IFirmInfoFormData['contactInfo']>({
    resolver: zodResolver(contactInfoSchema)
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createTenantSettingOverride] = useMutation(CREATE_TENANT_SETTING_OVERRIDE_MUTATION);
  const [updateSystemSetting] = useMutation(UPDATE_SYSTEM_SETTING_MUTATION);

  useEffect(() => {
    if (!contactInfoFormData) return;
    methods.reset(contactInfoFormData);
  }, [contactInfoFormData]);

  const onSubmit = async (data: IFirmInfoFormData['contactInfo']) => {
    try {
      setErrorMessage(null);

      if (firmInfoId) {
        await updateSystemSetting({
          variables: {
            data: {
              id: firmInfoId,
              value: JSON.stringify(data),
              description: ''
            }
          }
        });
      } else {
        await createTenantSettingOverride({
          variables: {
            data: {
              tenantId: tenantId || user?.tenant?.id,
              type: 'config',
              key: 'firmInfo',
              value: JSON.stringify(data)
            }
          }
        });
      }
      await firmInfoRefetch();
      handleCloseModalPage();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  return (
    <>
      {methods.formState.isSubmitting && (
        <ModalWrappedContent isOpen={methods.formState.isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseModalPage} backToTitle="Firm Info" />} />
        <PageTitle title="Contact Info" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <EditInvestorModalPageWrapper>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <EditContactInfoContentWrapper>
              <EditInvestorFormFieldsWrapper>
                <FormProvider {...methods}>
                  <AddressComponent
                    row={{
                      name: 'address',
                      component: 'address',
                      validation: {
                        required: true
                      }
                    }}
                    ignoreRequired
                  />
                  <ThreeColumnWrapper isMobile={isMobile}>
                    <PhoneFields>
                      <Controller
                        name="phone.countryCode"
                        control={methods.control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Select
                            label="Country Code"
                            value={value || ''}
                            setValue={onChange}
                            options={Object.entries(COUNTRY_CALLING_CODES_MAP).map(([key, value]) => ({ label: value, value: key }))}
                            errorText={error?.message}
                            size="md"
                            width="200px"
                            withSearch
                          />
                        )}
                      />
                      <Controller
                        name="phone.number"
                        control={methods.control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Input
                            label="Phone"
                            value={value || ''}
                            onChange={(e) => {
                              onChange(e.target.value);
                            }}
                            errorText={error?.message}
                            size="md"
                          />
                        )}
                      />
                    </PhoneFields>
                    <Controller
                      name="website"
                      control={methods.control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label="Website"
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.target.value);
                          }}
                          errorText={error?.message}
                          size="md"
                        />
                      )}
                    />
                    <Controller
                      name="email"
                      control={methods.control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label="Email"
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.target.value);
                          }}
                          errorText={error?.message}
                          size="md"
                        />
                      )}
                    />
                  </ThreeColumnWrapper>
                </FormProvider>
              </EditInvestorFormFieldsWrapper>
            </EditContactInfoContentWrapper>
            <FormFooter onCancel={handleCloseModalPage} disableSaveButton={methods.formState.isSubmitting} showRequiredFieldsWarning={false} />
          </form>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const EditContactInfoContentWrapper = styled.div`
  padding: 40px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;

const PhoneFields = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;
